import * as React from 'react';

import type {EntityAttribute, EntityAttributeValue} from 'nutshell-graphql-types';

import {EditableTextfieldContent} from '../../editable-textfield-content/editable-textfield-content';
import {Link} from '../../link/link';

import {AddressCustomField} from './components/address-custom-field';
import {CustomFieldCurrency} from './components/custom-field-currency';
import {DateCustomField} from './components/date-custom-field';
import {DecisionCustomField} from './components/decision-custom-field';
import {PhoneCustomField} from './components/phone-custom-field';
import {getNormalizedCustomField, getIsCustomFieldMultiLine} from './helpers';

type Props = {
    field: EntityAttribute;
    value: EntityAttributeValue | null | undefined;
    handleTextFieldUpdate: (
        newValue: string | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleEnumUpdate: (
        newValue: string[] | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleCurrencyUpdate: (
        currencyShortname: string,
        value: number | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleAddressUpdate: (
        address1: string,
        address2: string,
        address3: string,
        city: string,
        state: string,
        postalCode: string,
        country: string,
        field: EntityAttribute
    ) => Promise<any>;
    handlePhoneUpdate: (
        numberFormattedWithCountryCode: string,
        extension: string,
        field: EntityAttribute
    ) => Promise<any>;
    setIsSaving: (isSaving: boolean) => void;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
};

export function CustomFieldRowContent(props: Props) {
    const normalizedCustomField = React.useMemo(
        () => getNormalizedCustomField(props.field, props.value),
        [props.field, props.value]
    );

    const placeholder = `Add ${props.field.attribute.name}…`;

    function handleTextFieldUpdate(newValue?: string | null) {
        props.setIsSaving(true);
        props
            .handleTextFieldUpdate(newValue ? newValue.trim() : null, props.field)
            .then(() => {
                props.setIsEditing(false);
            })
            .finally(() => {
                props.setIsSaving(false);
            });
    }

    function handleEnumUpdate(newValue?: string[] | null) {
        props.setIsSaving(true);
        props
            .handleEnumUpdate(newValue, props.field)
            .then(() => {
                props.setIsEditing(false);
            })
            .finally(() => {
                props.setIsSaving(false);
            });
    }

    function handleCurrencyUpdate(currencyShortname: string, value?: number | null) {
        props.setIsSaving(true);
        props
            .handleCurrencyUpdate(currencyShortname, value, props.field)
            .then(() => {
                props.setIsEditing(false);
            })
            .finally(() => {
                props.setIsSaving(false);
            });
    }

    function handleAddressUpdate(
        address1: string,
        address2: string,
        address3: string,
        city: string,
        state: string,
        postalCode: string,
        country: string
    ) {
        props.setIsSaving(true);

        return props
            .handleAddressUpdate(
                address1,
                address2,
                address3,
                city,
                state,
                postalCode,
                country,
                props.field
            )
            .then(() => {
                props.setIsEditing(false);
            })
            .finally(() => {
                props.setIsSaving(false);
            });
    }

    function handlePhoneUpdate(numberFormattedWithCountryCode: string, extension: string) {
        props.setIsSaving(true);

        return props
            .handlePhoneUpdate(numberFormattedWithCountryCode, extension, props.field)
            .then(() => {
                props.setIsEditing(false);
            })
            .finally(() => {
                props.setIsSaving(false);
            });
    }

    const isMultiLine = React.useMemo(() => {
        return getIsCustomFieldMultiLine(props.field);
    }, [props.field]);

    return (
        <>
            {normalizedCustomField.type === 'text' && (
                <TextTypeWrapper
                    url={
                        props.value && props.value.__typename === 'EntityAttributeValueUrl'
                            ? props.value.value
                            : undefined
                    }
                    isEditing={props.isEditing}
                >
                    <EditableTextfieldContent
                        isEditing={props.isEditing}
                        setIsEditing={props.setIsEditing}
                        value={normalizedCustomField.value}
                        onUpdate={handleTextFieldUpdate}
                        placeholder={placeholder}
                        isMultiline={isMultiLine}
                        shouldLinkify={
                            props.field.attribute.type === 'STRING' ||
                            props.field.attribute.type === 'TEXT'
                        }
                    />
                </TextTypeWrapper>
            )}
            {normalizedCustomField.type === 'currency' && (
                <CustomFieldCurrency
                    currency={
                        (normalizedCustomField.value && normalizedCustomField.value.currency) ||
                        normalizedCustomField.defaultCurrency
                    }
                    amount={normalizedCustomField.value && normalizedCustomField.value.amount}
                    currencyOptions={normalizedCustomField.currencyOptions}
                    onUpdateCurrency={handleCurrencyUpdate}
                    isEditing={props.isEditing}
                    setIsEditing={props.setIsEditing}
                />
            )}
            {normalizedCustomField.type === 'enum' && (
                <div className='pt-2'>
                    <DecisionCustomField
                        value={normalizedCustomField.selected}
                        placeholder={`Select ${props.field.attribute.name}…`}
                        onUpdateValue={handleEnumUpdate}
                        options={normalizedCustomField.options}
                        allowMultiple={normalizedCustomField.allowMultiple}
                        isEditing={props.isEditing}
                        setIsEditing={props.setIsEditing}
                    />
                </div>
            )}
            {normalizedCustomField.type === 'date' && (
                <DateCustomField
                    date={normalizedCustomField.value}
                    placeholder={placeholder}
                    onUpdateDate={handleTextFieldUpdate}
                    isEditing={props.isEditing}
                    setIsEditing={props.setIsEditing}
                />
            )}
            {normalizedCustomField.type === 'address' && (
                <AddressCustomField
                    name={props.field.attribute.name}
                    addressValue={normalizedCustomField}
                    isEditing={props.isEditing}
                    setIsEditing={props.setIsEditing}
                    updateAddress={handleAddressUpdate}
                />
            )}
            {normalizedCustomField.type === 'phone' && (
                <PhoneCustomField
                    isEditing={props.isEditing}
                    setIsEditing={props.setIsEditing}
                    numberFormattedWithCountryCode={
                        normalizedCustomField.numberFormattedWithCountryCode
                    }
                    extension={normalizedCustomField.extension}
                    onChange={handlePhoneUpdate}
                    placeholder={placeholder}
                />
            )}
        </>
    );
}

type TextTypeWrapperProps = {
    url: string | null | undefined;
    isEditing: boolean;
    children: React.ReactNode;
};

function TextTypeWrapper(props: TextTypeWrapperProps) {
    if (props.url && !props.isEditing) {
        return (
            <Link
                href={props.url}
                newTab={true}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                widthFitContent={true}
                className='underline'
            >
                {props.children}
            </Link>
        );
    }

    return <>{props.children}</>;
}
